// Mugizh-admin Employee CRUD
// Manages employee master data (name + contact number only)
// Used by billing screen to tag which employee created a bill

import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { createOrUpdateEmployeeAction, getEmployeeListAction, getEmployeeSalesHistoryAction } from '../Store/actions/apparelAction';
import { deepCopyFunction } from '../Utils/helpers';
import AppConstant from '../Globals/AppConstants';
import { ConfirmDialog } from '../shared/confirm-dialog';
import { LoadingOverlay } from '../shared/loading-overlay';
import { Button } from 'ui/button';
import { Input } from 'ui/input';
import { Label } from 'ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'ui/sheet';
import { EmployeeTable } from './EmployeeTable';
import EmployeeSalesHistorySheet, { getCurrentMonth, getCurrentFinancialYear } from './EmployeeSalesHistorySheet';

const employeeTemp = {
  employeeId: "",
  name: null as any,
  mobileNumber: null as any,
  isActive: 1,
  isDeleted: 0,
  workTypeRefId: null as any,
  allowedLeaveDays: null as any,
};

const listTemp = { searchText: "", sortBy: 0, limit: 10, offset: 0 };

const Employee = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getEmployeeListOnLoad, getEmployeeListData, createOrUpdateEmployeeOnLoad } = apparelReducerState;
  const getEmployeeList = getEmployeeListData?.getEmployeeList?.employeeDetails;
  const getEmployeeListPage = getEmployeeListData?.getEmployeeList?.page;
  const workTypeOptions = AppConstant.workTypeOptions;

  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionKey, setActionKey] = useState<"2" | null>(null);
  const [listInputs, setListInputs] = useState<any>(deepCopyFunction(listTemp));
  // Arriving from Audit Log: re-run this screen's own search with the
  // record's business code, then open its edit Sheet once the matching row
  // (by employeeId, the uniqueKey alias) shows up in the list — Employee
  // has a real edit UI, so Audit Log goes straight to it.
  const [pendingAuditRecordId, setPendingAuditRecordId] = useState<string | undefined>(location.state?.auditRecordId);
  const [employeeInputs, setEmployeeInputs] = useState<any>(deepCopyFunction(employeeTemp));
  const [errors, setErrors] = useState<any>({});
  const [originalIsActive, setOriginalIsActive] = useState<number | null>(null);
  const [salesHistoryOpen, setSalesHistoryOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const { getEmployeeSalesHistoryOnload, getEmployeeSalesHistoryData } = apparelReducerState;
  const employeeSalesHistory = getEmployeeSalesHistoryData?.getEmployeeSalesHistory;

  const getEmployeeListApi = () => {
    dispatch(getEmployeeListAction({
      searchText: listInputs?.searchText,
      sortBy: listInputs?.sortBy,
      paging: { limit: listInputs?.limit, offset: listInputs?.offset },
    }));
  };

  useEffect(() => {
    getEmployeeListApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listInputs?.searchText, listInputs?.sortBy, listInputs?.offset]);

  useEffect(() => {
    if (location.state?.auditSearchCode) {
      setListInputs((s: any) => ({ ...s, searchText: location.state.auditSearchCode, offset: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pendingAuditRecordId || !getEmployeeList?.length) return;
    const match = getEmployeeList.find((e: any) => e.employeeId === pendingAuditRecordId);
    if (match) {
      openEdit(match);
      setPendingAuditRecordId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAuditRecordId, getEmployeeList]);

  const resetForm = () => {
    setEmployeeInputs(deepCopyFunction(employeeTemp));
    setErrors({});
  };

  const openAdd = () => {
    resetForm();
    setIsEdit(false);
    setOpen(true);
  };

  const openEdit = (item: any) => {
    setEmployeeInputs({
      employeeId: item?.employeeId,
      name: item?.name,
      mobileNumber: item?.mobileNumber,
      isActive: item?.isActive,
      isDeleted: 0,
      workTypeRefId: item?.workTypeRefId ?? null,
      allowedLeaveDays: item?.allowedLeaveDays ?? null,
    });
    setIsEdit(true);
    setOpen(true);
  };

  const validateForm = () => {
    let newErrors: any = {};
    if (!employeeInputs?.name?.trim()) newErrors.name = "Name is required";
    if (!employeeInputs?.mobileNumber?.trim()) newErrors.mobileNumber = "Mobile number is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => {
    return {
      employeeId: employeeInputs?.employeeId || "",
      name: employeeInputs?.name,
      mobileNumber: employeeInputs?.mobileNumber,
      isActive: employeeInputs?.isActive,
      isDeleted: employeeInputs?.isDeleted || 0,
      workTypeRefId: employeeInputs?.workTypeRefId || null,
      allowedLeaveDays: employeeInputs?.allowedLeaveDays || null,
    };
  };

  const createOrUpdateEmployeeApi = () => {
    if (!validateForm()) return;
    const payload = buildPayload();
    setPendingSaveKind(isEdit ? "edit" : "add");
    setCreateOrUpdateEmployeeLoad(true);
    dispatch(createOrUpdateEmployeeAction(payload) as any);
    setOpen(false);
    resetForm();
  };

  const handleInactivate = (item: any) => {
    setOriginalIsActive(item?.isActive);
    setEmployeeInputs({
      ...item,
      isActive: item?.isActive === 1 ? 0 : 1,
    });
    setActionKey("2");
    setIsModalOpen(true);
  };

  const confirmAction = () => {
    const payload = buildPayload();
    setPendingSaveKind("status");
    setCreateOrUpdateEmployeeLoad(true);
    dispatch(createOrUpdateEmployeeAction(payload) as any);
    setIsModalOpen(false);
    setActionKey(null);
    setOriginalIsActive(null);
  };

  const [createOrUpdateEmployeeLoad, setCreateOrUpdateEmployeeLoad] = useState(false);
  const [pendingSaveKind, setPendingSaveKind] = useState<"add" | "edit" | "status" | null>(null);

  useEffect(() => {
    if (createOrUpdateEmployeeLoad && !createOrUpdateEmployeeOnLoad) {
      setCreateOrUpdateEmployeeLoad(false);
      getEmployeeListApi();
      toast.success(
        pendingSaveKind === "status" ? "Employee status updated successfully"
          : pendingSaveKind === "edit" ? "Employee updated successfully"
            : "Employee added successfully"
      );
      setPendingSaveKind(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrUpdateEmployeeLoad, createOrUpdateEmployeeOnLoad]);

  const SALES_HISTORY_PAGE_SIZE = 10;

  const handleViewSalesHistory = (item: any) => {
    setSelectedEmployee(item);
    setSalesHistoryOpen(true);
    // Dispatch action to fetch sales history for the current month/financial year
    dispatch(getEmployeeSalesHistoryAction({
      employeeId: item?.employeeId,
      month: getCurrentMonth(),
      financialYear: getCurrentFinancialYear(),
      limit: SALES_HISTORY_PAGE_SIZE,
      offset: 0,
    } as any));
  };

  const handleSalesHistoryMonthChange = (month: string, financialYear: number) => {
    if (selectedEmployee) {
      dispatch(getEmployeeSalesHistoryAction({
        employeeId: selectedEmployee?.employeeId,
        month: month,
        financialYear: financialYear,
        limit: SALES_HISTORY_PAGE_SIZE,
        offset: 0,
      } as any));
    }
  };

  const handleSalesHistoryPageChange = (month: string, financialYear: number, page: number) => {
    if (selectedEmployee) {
      dispatch(getEmployeeSalesHistoryAction({
        employeeId: selectedEmployee?.employeeId,
        month: month,
        financialYear: financialYear,
        limit: SALES_HISTORY_PAGE_SIZE,
        offset: (page - 1) * SALES_HISTORY_PAGE_SIZE,
      } as any));
    }
  };

  return (
    <div className="flex flex-col h-full">
      <LoadingOverlay loading={getEmployeeListOnLoad} />

      <div className="flex-1 flex flex-col gap-4">
        <EmployeeTable
          data={getEmployeeList || []}
          loading={getEmployeeListOnLoad}
          searchValue={listInputs?.searchText}
          onSearch={(searchText) => setListInputs({ ...listInputs, searchText, offset: 0 })}
          onEdit={openEdit}
          onToggleStatus={handleInactivate}
          onViewSalesHistory={handleViewSalesHistory}
          onAdd={openAdd}
          pagination={{
            page: Math.floor(listInputs?.offset / listInputs?.limit) + 1,
            total: getEmployeeListPage?.totalRecords || 0,
            onChange: (page) => setListInputs({ ...listInputs, offset: (page - 1) * listInputs?.limit }),
          }}
        />
      </div>

      {/* Add/Edit Sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{isEdit ? "Edit Employee" : "Add Employee"}</SheetTitle>
          </SheetHeader>
          <div className="space-y-6 mt-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={employeeInputs?.name || ""}
                onChange={(e) => setEmployeeInputs({ ...employeeInputs, name: e.target.value })}
                placeholder="Enter employee name"
                disabled={createOrUpdateEmployeeLoad}
              />
              {errors?.name && <p className="text-xs text-red-500">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile Number *</Label>
              <Input
                id="mobile"
                value={employeeInputs?.mobileNumber || ""}
                onChange={(e) => setEmployeeInputs({ ...employeeInputs, mobileNumber: e.target.value })}
                placeholder="Enter mobile number"
                disabled={createOrUpdateEmployeeLoad}
              />
              {errors?.mobileNumber && <p className="text-xs text-red-500">{errors.mobileNumber}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="workType">Work Type</Label>
              <Select
                value={employeeInputs?.workTypeRefId ? String(employeeInputs.workTypeRefId) : undefined}
                onValueChange={(value) => setEmployeeInputs({ ...employeeInputs, workTypeRefId: parseInt(value, 10) })}
                disabled={createOrUpdateEmployeeLoad}
              >
                <SelectTrigger id="workType">
                  <SelectValue placeholder="Select work type" />
                </SelectTrigger>
                <SelectContent>
                  {workTypeOptions?.map((opt: any) => (
                    <SelectItem key={opt?.id} value={String(opt?.id)}>
                      {opt?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="allowedLeaveDays">Allowed Leave Days</Label>
              <Input
                id="allowedLeaveDays"
                type="number"
                min={0}
                value={employeeInputs?.allowedLeaveDays ?? ""}
                onChange={(e) =>
                  setEmployeeInputs({
                    ...employeeInputs,
                    allowedLeaveDays: e.target.value === "" ? null : parseInt(e.target.value, 10),
                  })
                }
                placeholder="Enter allowed leave days"
                disabled={createOrUpdateEmployeeLoad}
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={createOrUpdateEmployeeApi}
                disabled={createOrUpdateEmployeeLoad}
                className="flex-1"
              >
                {createOrUpdateEmployeeLoad ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={isModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsModalOpen(false);
            setActionKey(null);
            setOriginalIsActive(null);
          }
        }}
        title={originalIsActive === 1 ? "Inactivate Employee" : "Activate Employee"}
        description={`Are you sure you want to ${originalIsActive === 1 ? "inactivate" : "activate"} ${employeeInputs?.name}?`}
        onConfirm={confirmAction}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
      />

      {/* Sales History Sheet */}
      <EmployeeSalesHistorySheet
        open={salesHistoryOpen}
        onClose={() => setSalesHistoryOpen(false)}
        employee={selectedEmployee}
        salesData={employeeSalesHistory}
        loading={getEmployeeSalesHistoryOnload}
        onMonthChange={handleSalesHistoryMonthChange}
        onPageChange={handleSalesHistoryPageChange}
        pageSize={SALES_HISTORY_PAGE_SIZE}
      />
    </div>
  );
};

export default Employee;
